export type IdentityPair = readonly [string, string];

function cleanIdentityId(value: string) {
  return String(value || "").trim();
}

/**
 * Canonical ordering used by the backend before deriving the deterministic DM
 * document ID. This helper intentionally does not hash: conversation IDs are
 * backend-owned and clients should not be able to mint conversations directly.
 */
export function canonicalIdentityPair(left: string, right: string): IdentityPair {
  const a = cleanIdentityId(left);
  const b = cleanIdentityId(right);

  if (!a || !b) {
    throw new Error("Deux identités valides sont requises pour un message direct.");
  }
  if (a === b) {
    throw new Error("Une identité ne peut pas ouvrir un message direct avec elle-même.");
  }

  return a < b ? [a, b] : [b, a];
}

export function isIdentityParticipant(
  participantIdentityIds: readonly string[],
  identityId: string
) {
  const target = cleanIdentityId(identityId);
  return !!target && participantIdentityIds.includes(target);
}

export function otherParticipantIdentityId(
  participantIdentityIds: readonly string[],
  identityId: string
) {
  const target = cleanIdentityId(identityId);
  if (!target || participantIdentityIds.length !== 2) return null;
  if (!participantIdentityIds.includes(target)) return null;
  return participantIdentityIds[0] === target
    ? participantIdentityIds[1] || null
    : participantIdentityIds[0] || null;
}

export function identityInboxStorageKey(identityId: string) {
  const clean = cleanIdentityId(identityId);
  if (!clean) throw new Error("Identité invalide.");
  return `@neqraw/identity-inbox/v1/${encodeURIComponent(clean)}`;
}

export function isIdentityDmUnread(
  thread: { lastMessageAt?: number | null; lastMessageSenderIdentityId?: string | null; lastReadAt?: number | null },
  identityId: string,
) {
  return !!thread.lastMessageAt
    && thread.lastMessageSenderIdentityId !== identityId
    && thread.lastMessageAt > (thread.lastReadAt || 0);
}

export function scopeIdentityDmInbox<T extends {
  identityId: string;
  threads: unknown[];
  peers: Record<string, unknown>;
}>(inbox: T, activeIdentityId: string | null): T {
  return inbox.identityId === activeIdentityId
    ? inbox
    : { ...inbox, identityId: activeIdentityId || "", threads: [], peers: {} };
}
